import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dispatch, SetStateAction } from "react";

export interface IManualCptEntry {
  id: string;
  cptCode: string;
  description: string;
  price: number | null;
}

export const createEmptyManualCptEntry = (): IManualCptEntry => ({
  id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`,
  cptCode: "",
  description: "",
  price: null,
});

interface IManualCptEntryProps {
  entries: IManualCptEntry[];
  setEntries: Dispatch<SetStateAction<IManualCptEntry[]>>;
}

export function ManualCptEntry({ entries, setEntries }: IManualCptEntryProps) {
  const updateEntry = <K extends keyof Omit<IManualCptEntry, "id">>(
    id: string,
    field: K,
    value: IManualCptEntry[K]
  ) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const removeEntry = (id: string) => {
    setEntries((prev) =>
      prev.length === 1 ? prev : prev.filter((entry) => entry.id !== id)
    );
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, createEmptyManualCptEntry()]);
  };

  return (
    <div className="flex flex-column gap-3">
      {entries.map((entry, index) => (
        <div
          key={entry.id}
          className="border-1 border-round surface-border surface-50 p-4"
        >
          <div className="flex justify-content-between align-items-center mb-3">
            <span className="text-color-secondary">CPT Entry {index + 1}</span>
            <Button
              type="button"
              icon="pi pi-trash"
              className="p-button-text p-button-rounded p-button-secondary p-button-danger"
              disabled={entries.length === 1}
              onClick={() => removeEntry(entry.id)}
            />
          </div>

          <div className="formgrid grid">
            <div className="field col-12 md:col-4">
              <Label htmlFor={`cptCode-${entry.id}`}>
                CPT Code <span className="text-red-500">*</span>
              </Label>
              <InputText
                id={`cptCode-${entry.id}`}
                className="w-full"
                placeholder="e.g., 99213"
                value={entry.cptCode}
                onChange={(e) =>
                  updateEntry(entry.id, "cptCode", e.target.value)
                }
              />
            </div>

            <div className="field col-12 md:col-4">
              <Label htmlFor={`description-${entry.id}`}>Description</Label>
              <InputText
                id={`description-${entry.id}`}
                className="w-full"
                placeholder="Enter CPT description"
                value={entry.description}
                onChange={(e) =>
                  updateEntry(entry.id, "description", e.target.value)
                }
              />
            </div>

            <div className="field col-12 md:col-4">
              <Label htmlFor={`price-${entry.id}`}>
                Price <span className="text-red-500">*</span>
              </Label>
              <InputNumber
                inputId={`price-${entry.id}`}
                className="w-full"
                inputClassName="w-full"
                placeholder="e.g., $150.00"
                value={entry.price}
                currency="USD"
                locale="en-US"
                mode="currency"
                onValueChange={(e) =>
                  updateEntry(entry.id, "price", e.value ?? null)
                }
              />
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        label="Add Another CPT Code"
        icon="pi pi-plus"
        className="p-button-outlined p-button-secondary w-full"
        style={{ borderStyle: "dashed" }}
        onClick={addEntry}
      />
    </div>
  );
}
